import { DevTool } from '@hookform/devtools';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import {
  FormProvider,
  formValuesFromProDirectory,
  formValuesFromRevisionData,
  ProDirectoryFormSkeleton,
  ProDirectoryFormSteps,
  ProDirectoryStepFooter,
  ProDirectoryStepperHeader,
  TProDirectoryAccountProfile,
  TProfileSocials,
  toUpdateProDirectoryPayload,
  useProDirectoryForm,
} from '@onesource/components';
import {
  useGetProDirectoryPendingRevisionQuery,
  useGetProDirectoryQuery,
  useUpdateProDirectoryMutation,
} from '@onesource/services';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { SHOW_DEV_TOOLS } from 'src/config';

import { ProDirectoryFooterActions } from './footer-actions';
import { ProDirectoryRevisionsDialog } from './revisions-dialog';

const FORM_GUIDE_URL =
  'https://assets.restoreyourcore.com/pro-directory/working-with-the-pro-directory-profile-form-v1.0.pdf';
const BRAND_GUIDELINES_URL =
  'https://members.restoreyourcore.com/programs/ryc-pro-collective/marketing-resources/brand-guidelines/pdf/ryc-brand-assets-guidelines';

export type TProDirectoryTabProps = {
  profileFullName: string;
  profileBio: string;
  profilePhone: string;
  profileSocials: TProfileSocials;
  profileAvatar: string;
  stepperSlot?: HTMLDivElement | null;
  isTabActive: boolean;
};

export const ProDirectoryTab: FC<TProDirectoryTabProps> = ({
  profileFullName,
  profileBio,
  profilePhone,
  profileSocials,
  profileAvatar,
  stepperSlot,
  isTabActive,
}) => {
  const { data: proDirectory, isLoading } = useGetProDirectoryQuery();
  const { data: pendingRevision } = useGetProDirectoryPendingRevisionQuery();
  const { mutateAsync, isPending } = useUpdateProDirectoryMutation();
  const [revisionsOpen, setRevisionsOpen] = useState(false);

  // The loaded baseline the form is hydrated from: an in-progress revision wins
  // over the approved listing. `null` keeps the empty defaults (brand-new profile).
  const baselineValues = useMemo(() => {
    if (pendingRevision) {
      return formValuesFromRevisionData(pendingRevision.data as Record<string, unknown>);
    }
    if (proDirectory) {
      return formValuesFromProDirectory(proDirectory as unknown as Record<string, unknown>);
    }
    return null;
  }, [proDirectory, pendingRevision]);

  const accountProfile = useMemo<TProDirectoryAccountProfile>(
    () => ({
      fullName: profileFullName,
      bio: profileBio,
      phone: profilePhone,
      socials: profileSocials,
      avatar: profileAvatar,
    }),
    [profileFullName, profileBio, profilePhone, profileSocials, profileAvatar],
  );

  const form = useProDirectoryForm({ baselineValues, accountProfile });
  const { methods, activeStep, setActiveStep, isComplete, isActuallyDirty } = form;
  const { handleSubmit, getValues } = methods;

  // Tab stays mounted (display:none) when switching away. Reset to the first
  // step on leave (while hidden) so re-entry doesn't flash the last step.
  useEffect(() => {
    if (!isTabActive) {
      setActiveStep(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTabActive]);

  const onSubmitForReview = handleSubmit(async data => {
    await mutateAsync(toUpdateProDirectoryPayload(data, false));
  });

  const onSaveDraft = useCallback(async () => {
    await mutateAsync(toUpdateProDirectoryPayload(getValues(), true));
  }, [mutateAsync, getValues]);

  const handleRevisionsOpen = useCallback(() => setRevisionsOpen(true), []);

  const saveDisabled = isPending || !isActuallyDirty;
  const hasPendingRevision = !!pendingRevision;
  // Memoised so the memoised footer's shallow prop compare holds between keystrokes.
  const footerActions = useMemo(
    () => (
      <ProDirectoryFooterActions
        isPending={isPending}
        saveDisabled={saveDisabled}
        hasPendingRevision={hasPendingRevision}
        onRevisionsClick={handleRevisionsOpen}
        onSaveDraft={onSaveDraft}
      />
    ),
    [isPending, saveDisabled, hasPendingRevision, handleRevisionsOpen, onSaveDraft],
  );

  if (isLoading) {
    return <ProDirectoryFormSkeleton />;
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <ProDirectoryRevisionsDialog
        open={revisionsOpen}
        onClose={() => setRevisionsOpen(false)}
        approvedData={proDirectory ?? null}
      />

      {stepperSlot &&
        createPortal(
          <ProDirectoryStepperHeader
            activeStep={activeStep}
            completedSteps={form.completedSteps}
            onStepClick={form.handleStepClick}
            isStepEnabled={form.isStepEnabled}
          />,
          stepperSlot,
        )}

      <FormProvider
        methods={methods}
        onSubmit={onSubmitForReview}
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          '& form': { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
          }}
        >
          {/* Top: progress + stepper nav (falls back inline when no slot, e.g. below the lg breakpoint) */}
          <Box sx={{ flexShrink: 0 }}>
            <LinearProgress sx={{ mb: 1.5, borderRadius: 1, visibility: isPending ? 'visible' : 'hidden' }} />
            {!stepperSlot && (
              <ProDirectoryStepperHeader
                activeStep={activeStep}
                completedSteps={form.completedSteps}
                onStepClick={form.handleStepClick}
                isStepEnabled={form.isStepEnabled}
              />
            )}
          </Box>

          {/* Middle: scrollable step body */}
          <ProDirectoryFormSteps
            form={form}
            isPending={isPending}
            intro={
              // Static welcome / migration notice, shown only on the "About Me" step. Scrolls with the step body.
              <Box
                sx={{
                  mb: 2,
                  px: { xs: 2, sm: 2.5 },
                  py: 2,
                  borderRadius: 2,
                  bgcolor: theme => alpha(theme.palette.primary.main, 0.08),
                }}
              >
                <Typography variant="subtitle1" fontWeight={700} color="primary.main" sx={{ mb: 0.75 }}>
                  Welcome! Let&apos;s build your RYC® Pro Directory profile
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Complete the 4 steps at the top of this page: <strong>About Me</strong>, <strong>What I Offer</strong>
                  , <strong>How to Find Me</strong>, and <strong>Preview</strong>. If you had an existing profile, your
                  information has been migrated. Please review it, update anything that&apos;s changed, and fill in the
                  new required fields.
                </Typography>
                <Typography variant="body2" color="text.primary" sx={{ mb: 1 }}>
                  Not sure how a field works? See the{' '}
                  <Link href={FORM_GUIDE_URL} target="_blank" rel="noopener noreferrer">
                    step-by-step form guide
                  </Link>
                  .
                </Typography>
                <Typography variant="body2" color="text.primary">
                  Before you submit, open the <strong>Preview</strong> and read your profile as a client would see it.
                  Then check it against the{' '}
                  <Link href={BRAND_GUIDELINES_URL} target="_blank" rel="noopener noreferrer">
                    RYC® Brand Guidelines
                  </Link>{' '}
                  requirements. Every profile is reviewed against these before it&apos;s published. A quick check now
                  saves a round of edits later.
                </Typography>
              </Box>
            }
          />

          {/* Bottom: actions */}
          <ProDirectoryStepFooter
            activeStep={activeStep}
            isPending={isPending}
            continueDisabled={isPending || (activeStep === 2 && !isComplete)}
            // Enabled purely on completeness: every required field must have a value.
            submitDisabled={isPending || !isComplete}
            submitLabel="Submit Application"
            allIssues={form.allIssues}
            issuesTitle="In order to Submit the Application you must fix the following errors:"
            issuesHint="You can still Save Draft and finish later."
            extraActions={footerActions}
            onBack={form.handleBack}
            onContinue={form.handleContinue}
          />
        </Box>

        {SHOW_DEV_TOOLS && <DevTool id="account-pro-directory" control={methods.control} />}
      </FormProvider>
    </Box>
  );
};
